/**
 * A small, fixed-size rolling log of "what the user did just before a crash".
 *
 * The tracker pushes a breadcrumb whenever it detects a click, navigation,
 * console error, or network error. When an error is captured we attach a
 * snapshot of the buffer so each report carries its lead-up context.
 *
 * Pure and dependency-free; covered by __tests__/breadcrumbs.test.ts.
 */

export type BreadcrumbType = 'click' | 'navigation' | 'console' | 'network';

export interface Breadcrumb {
    type: BreadcrumbType;
    message: string;
    timestampMs: number;
    data?: Record<string, unknown>;
}

const DEFAULT_MAX_BREADCRUMBS = 50;

export class BreadcrumbBuffer {
    private readonly max: number;
    private items: Breadcrumb[] = [];

    constructor(max: number = DEFAULT_MAX_BREADCRUMBS) {
        this.max = max > 0 ? max : DEFAULT_MAX_BREADCRUMBS;
    }

    /** Append a breadcrumb, evicting the oldest once the buffer is full. */
    add(crumb: Breadcrumb): void {
        this.items.push(crumb);
        if (this.items.length > this.max) {
            this.items.splice(0, this.items.length - this.max);
        }
    }

    /** Return a shallow copy so callers can't mutate the live buffer. */
    snapshot(): Breadcrumb[] {
        return this.items.map((c) => ({ ...c }));
    }

    clear(): void {
        this.items = [];
    }

    get size(): number {
        return this.items.length;
    }
}
